import { ArrowRight, Wallet } from "lucide-react";
import { OFFICIAL_SITE, WALLETS } from "../../data/site";
import { useLanguage } from "../../i18n/LanguageContext";
import { Reveal } from "./Reveal";
import { SectionHeader } from "./SectionHeader";

const FEES = [
    { label: "System Fee", value: "0.50 USDT", unit: "BEP20", desc: "System maintenance & platform fee" },
    { label: "Support DeFi", value: "Included", unit: "", desc: "DeFi & community support" },
    { label: "Network Fee", value: "$0.50 BNB", unit: "", desc: "Blockchain network fee" },
];

export const JoiningInfo = () => {
    const { t } = useLanguage();
    return (
        <section id="join" data-testid="joining-section" className="relative overflow-hidden border-t border-white/5 bg-nx-alt py-20 sm:py-24 lg:py-32">
            <div className="bg-grid-fine absolute inset-0 opacity-50" aria-hidden="true" style={{ maskImage: "radial-gradient(ellipse 70% 60% at 50% 0%, black 20%, transparent 70%)", WebkitMaskImage: "radial-gradient(ellipse 70% 60% at 50% 0%, black 20%, transparent 70%)" }} />
            <div className="relative mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
                <SectionHeader
                    label={t("Joining Information")}
                    testId="joining"
                    title={<>{t("Join Nexora Global")}</>}
                    description={t("One-time payment. BEP20 network supported — all major wallets supported.")}
                />

                <div className="mt-14 grid grid-cols-1 gap-4 lg:grid-cols-12 lg:gap-5">
                    <Reveal className="lg:col-span-5">
                        <div className="flex h-full flex-col justify-between rounded-2xl border border-nx-lime/25 bg-nx-lime/[0.05] p-8 lg:p-10" data-testid="joining-amount-card">
                            <div>
                                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-nx-mute">{t("Minimum Joining Amount")}</span>
                                <div className="mt-4 flex items-baseline gap-2">
                                    <span className="text-6xl font-extrabold tracking-tight text-nx-lime tabular">5</span>
                                    <span className="text-xl font-extrabold text-nx-text">USDT</span>
                                    <span className="rounded-full border border-white/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-nx-sub">BEP20</span>
                                </div>
                                <p className="mt-4 text-sm leading-relaxed text-nx-sub">{t("For the Next Global Core Fund — one-time payment, lifetime opportunity.")}</p>
                            </div>
                            <a
                                href={OFFICIAL_SITE}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="joining-join-button"
                                className="btn-sweep group mt-8 inline-flex w-fit items-center gap-2 rounded-full bg-nx-lime px-7 py-3.5 text-sm font-extrabold tracking-wide text-nx-base shadow-[0_6px_32px_rgba(184,255,24,0.22)] transition-all duration-300 hover:brightness-110 hover:-translate-y-px"
                            >
                                {t("Join Nexora")}
                                <ArrowRight size={16} strokeWidth={2} className="transition-transform duration-300 group-hover:translate-x-0.5 rtl:rotate-180" aria-hidden="true" />
                            </a>
                        </div>
                    </Reveal>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:col-span-7 lg:gap-5">
                        {FEES.map((f, i) => (
                            <Reveal key={f.label} delay={0.1 + i * 0.08} className="h-full">
                                <div className="flex h-full flex-col rounded-2xl border border-white/5 bg-nx-elev p-6 lg:p-7" data-testid={`fee-${f.label.toLowerCase().replace(/\s+/g, "-")}`}>
                                    <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-nx-mute">{t(f.label)}</span>
                                    <span className="mt-4 text-2xl font-extrabold tracking-tight text-nx-text tabular">
                                        {f.value === "Included" ? t("Included") : f.value} {f.unit && <span className="text-sm font-bold text-nx-sub">{f.unit}</span>}
                                    </span>
                                    <p className="mt-auto pt-4 text-xs leading-relaxed text-nx-mute">{t(f.desc)}</p>
                                </div>
                            </Reveal>
                        ))}
                        <Reveal delay={0.3} className="sm:col-span-3">
                            <div className="rounded-2xl border border-white/5 bg-nx-elev p-6 lg:p-7" data-testid="supported-wallets">
                                <div className="flex items-center gap-2.5">
                                    <Wallet size={16} strokeWidth={1.5} className="text-nx-lime" aria-hidden="true" />
                                    <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-nx-mute">{t("Supported Wallets")}</span>
                                </div>
                                <div className="mt-4 flex flex-wrap gap-2">
                                    {WALLETS.map((w) => (
                                        <span key={w} className="rounded-full border border-white/10 bg-nx-soft px-4 py-2 text-xs font-bold text-nx-text">
                                            {w}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
};
